import { useCallback, useMemo } from 'react';
import useUser from '@alltrails/context/hooks/useUser';
import logMapBaseLayerTapped from '@alltrails/amplitude/events/logMapBaseLayerTapped';
import logMapOverlayTapped from '@alltrails/amplitude/events/logMapOverlayTapped';
import MapOverlay from '@alltrails/amplitude/enums/MapOverlay';
import MapBaseLayer from '@alltrails/amplitude/enums/MapBaseLayer';
import { useClientNavigationPathname } from '@alltrails/core';
import type { FeatureEntitlement } from '@alltrails/types/FeatureEntitlement';
import { DynamicStyleId, StyleId } from '../types/Styles';
import { baseStyleCardConfigs, dynamicStyleCardConfigs, staticStyleCardConfigs } from '../utils/styleCardConfigs';
import { useAppDispatch, useAppSelector } from '../redux';
import { toggleDynamicStyleId, toggleStaticStyleId, updateBaseStyleId, updateDynamicStyleIds } from '../redux/reducer';
import adminUrlIsValid from '../utils/adminUrlIsValid';
import { isBaseStyle, isDynamicStyle, isStaticStyle } from '../utils/styleIdHelpers';
import { getMapBaseLayer, getMapDrawerLocation, getMapOverlay, getPage } from '../utils/amplitudeHelpers';
import { HandleStyleRoadblock } from '../types/Roadblock';
import { conditionsStyleIds } from '../utils/constants';

// With the new layer menu, users can only have one of the single select ids applied at a time
const singleSelectDynamicIds: DynamicStyleId[] = ['newHeatmap', ...conditionsStyleIds];

type UseStyleSelectionArgs = {
  handleFailedEntitlementCheck: (entitlement: FeatureEntitlement, applyStyle: () => void) => void;
  handleRoadblock: HandleStyleRoadblock;
  onMapBaseLayerTapped?: (baseLayer: MapBaseLayer | undefined) => void;
  onMapOverlayTapped?: (overlay: MapOverlay | undefined, isAddition: boolean) => void;
  disableAnalytics?: boolean;
};

const useStyleSelection = ({
  handleFailedEntitlementCheck,
  onMapBaseLayerTapped,
  onMapOverlayTapped,
  disableAnalytics,
  handleRoadblock
}: UseStyleSelectionArgs) => {
  const dispatch = useAppDispatch();
  const user = useUser();
  const pathname = useClientNavigationPathname();

  const { adminStyleSettings, baseStyleId, dynamicStyleIds, staticStyleIds } = useAppSelector(state => ({
    adminStyleSettings: state.map.adminStyleSettings,
    baseStyleId: state.map.baseStyleId,
    dynamicStyleIds: state.map.dynamicStyleIds,
    staticStyleIds: state.map.staticStyleIds,
    is3dActive: state.map.is3dActive
  }));
  const page = getPage(pathname);

  const onStyleSelect = useCallback(
    (id: StyleId) => {
      if (isBaseStyle(id)) {
        if (id === baseStyleId) {
          // Do nothing if base style has already been selected
          return;
        }
        if (baseStyleCardConfigs[id].requiresAdminStyleSourceUrl && !adminUrlIsValid(adminStyleSettings?.styleSourceUrls?.[id])) {
          return;
        }

        if (!user) {
          handleRoadblock();
          return;
        }

        const mapBaseLayer = getMapBaseLayer(id);
        if (mapBaseLayer && !disableAnalytics) {
          logMapBaseLayerTapped({ map_base_layer: mapBaseLayer, map_drawer_location: 'base map', page });
        }

        if (mapBaseLayer && onMapBaseLayerTapped) {
          onMapBaseLayerTapped(mapBaseLayer);
        }

        dispatch(updateBaseStyleId(id));
      } else if (
        isStaticStyle(id) &&
        staticStyleCardConfigs[id].requiresAdminStyleSourceUrl &&
        !adminUrlIsValid(adminStyleSettings?.styleSourceUrls?.[id])
      ) {
        return;
      } else {
        if (isStaticStyle(id)) {
          if (!user) {
            handleRoadblock();
            return;
          }

          if (!disableAnalytics) {
            logMapOverlayTapped({
              is_addition: !staticStyleIds.includes(id),
              map_location: page,
              map_drawer_location: getMapDrawerLocation(id),
              map_overlay: getMapOverlay(id) ?? ('' as MapOverlay)
            });
          }

          if (onMapOverlayTapped) {
            onMapOverlayTapped(getMapOverlay(id), !staticStyleIds.includes(id));
          }
          dispatch(toggleStaticStyleId(id));
        } else if (isDynamicStyle(id)) {
          const config = dynamicStyleCardConfigs[id];

          if (!user) {
            handleRoadblock();
            return;
          }

          const isAddition = !dynamicStyleIds.includes(id);

          if (!disableAnalytics) {
            logMapOverlayTapped({
              is_addition: isAddition,
              map_location: page,
              map_drawer_location: getMapDrawerLocation(id),
              map_overlay: getMapOverlay(id) ?? ('' as MapOverlay)
            });

            if (isAddition) {
              window.Sprig?.('identifyAndTrack', {
                eventName: 'Map_Overlay_Tapped',
                properties: {
                  is_addition: isAddition,
                  map_location: page,
                  map_drawer_location: getMapDrawerLocation(id),
                  map_overlay: getMapOverlay(id) ?? ('' as MapOverlay)
                }
              });
            }
          }

          if (onMapOverlayTapped) {
            onMapOverlayTapped(getMapOverlay(id), isAddition);
          }

          const updateStyles = () => {
            const idIsSingleSelect = singleSelectDynamicIds.includes(id);
            if (idIsSingleSelect && isAddition) {
              // We are enabling a single select id, so remove any already-applied single select ids
              const nonRestrictedDynamicIds = dynamicStyleIds.filter(id => !singleSelectDynamicIds.includes(id));
              dispatch(updateDynamicStyleIds([...nonRestrictedDynamicIds, id]));
            } else {
              dispatch(toggleDynamicStyleId(id));
            }
          };

          if (isAddition && config.featureEntitlement && !user.featureEntitlements?.includes(config.featureEntitlement)) {
            // User in peak experiment is trying to apply a style that they don't have access to
            handleFailedEntitlementCheck(config.featureEntitlement, updateStyles);
          } else {
            updateStyles();
          }
        }
      }
    },
    [
      adminStyleSettings?.styleSourceUrls,
      baseStyleId,
      user,
      disableAnalytics,
      onMapBaseLayerTapped,
      dispatch,
      handleRoadblock,
      page,
      onMapOverlayTapped,
      staticStyleIds,
      dynamicStyleIds,
      handleFailedEntitlementCheck
    ]
  );

  return useMemo(
    () => ({
      onStyleSelect
    }),
    [onStyleSelect]
  );
};

export default useStyleSelection;
